import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader, Loading, Empty } from "../components/Layout";
import { toast } from "sonner";
import { Radar, AlertTriangle, Clock, ShieldAlert } from "lucide-react";

const SEV = {
  fatality: ["FATALITY", "#B23A2E"], incident: ["INCIDENT", "#E2711D"],
  citation: ["CITATION", "#3E8F99"], conflict_exposure: ["CONFLICT EXPOSURE", "#1C2530"],
};

function ReviewForm({ opp, onApprove, onDiscard, busy }) {
  const [modules, setModules] = useState("");
  const [delivery, setDelivery] = useState("Blended");
  const [specifics, setSpecifics] = useState("");
  const quietActive = opp.quiet_until && new Date(opp.quiet_until) > new Date();
  return (
    <div className="mt-3 pt-3 border-t border-[#DFDAD0]">
      {quietActive && (
        <div className="flex items-center gap-2 mb-3 text-[#B23A2E] font-mono text-[11px]">
          <ShieldAlert className="w-4 h-4" /> QUIET PERIOD ACTIVE until {new Date(opp.quiet_until).toLocaleString()} — outreach blocked (fatality guardrail)
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
        <div>
          <label className="font-mono text-[10px] uppercase text-[#1C2530]/60">Modules to reference *</label>
          <input value={modules} onChange={(e) => setModules(e.target.value)} data-testid={`opp-modules-${opp.id}`}
            placeholder="e.g. EHS VR simulation" className="w-full mt-1 border border-[#DFDAD0] px-2 py-1 text-xs outline-none focus:border-[#1C2530]" />
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-[#1C2530]/60">Delivery mode</label>
          <select value={delivery} onChange={(e) => setDelivery(e.target.value)} data-testid={`opp-delivery-${opp.id}`}
            className="w-full mt-1 border border-[#DFDAD0] px-2 py-1 text-xs bg-white outline-none focus:border-[#1C2530]">
            {["Classroom", "AR-VR", "Simulation", "Blended"].map((d) => <option key={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono text-[10px] uppercase text-[#1C2530]/60">Pitch specifics</label>
          <input value={specifics} onChange={(e) => setSpecifics(e.target.value)} placeholder="optional"
            className="w-full mt-1 border border-[#DFDAD0] px-2 py-1 text-xs outline-none focus:border-[#1C2530]" />
        </div>
      </div>
      <div className="flex gap-2">
        <button disabled={busy || quietActive || !modules.trim()} data-testid={`opp-approve-${opp.id}`}
          onClick={() => onApprove(opp.id, { modules, delivery_mode: delivery, specifics })}
          className="bg-[#1C2530] text-[#F5F3EE] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40">
          Approve → create lead
        </button>
        <button disabled={busy} onClick={() => onDiscard(opp.id)} data-testid={`opp-discard-${opp.id}`}
          className="border border-[#DFDAD0] px-3 py-1.5 text-xs font-mono uppercase tracking-wider hover:bg-[#EFECE5]">
          Discard
        </button>
      </div>
    </div>
  );
}

export default function Opportunities() {
  const [opps, setOpps] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/opportunities").then((r) => setOpps(r.data));
  useEffect(() => { load(); }, []);

  const scan = async () => { setBusy(true); try { const r = await api.post("/opportunities/scan"); toast.success(`${r.data.created} opportunities surfaced`); await load(); } catch { toast.error("Scan failed"); } finally { setBusy(false); } };
  const approve = async (id, body) => { setBusy(true); try { const r = await api.post(`/opportunities/${id}/approve`, body); toast.success("Lead created at Researched"); await load(); nav(`/leads/${r.data.lead_id}`); } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); } finally { setBusy(false); } };
  const discard = async (id) => { setBusy(true); try { await api.post(`/opportunities/${id}/discard`); toast.success("Discarded"); await load(); } catch { toast.error("Failed"); } finally { setBusy(false); } };

  return (
    <>
      <PageHeader eyebrow="NEWS / GDELT · MANDATORY SUPERVISOR GATE · NEVER AUTO-SEND" title="Event-Triggered Opportunities" hint="Leads created automatically from news signals (funding, expansion, leadership change). Nothing is sent until a Sales Lead reviews and assigns them from the Allocation Queue."
        right={<button onClick={scan} disabled={busy} data-testid="scan-btn" className="flex items-center gap-2 bg-[#1C2530] text-[#F5F3EE] px-4 py-2 text-xs font-mono uppercase tracking-wider hover:bg-[#2a3644] disabled:opacity-40"><Radar className="w-4 h-4" /> {busy ? "Scanning…" : "Scan Feed"}</button>} />
      <div className="p-8 space-y-4">
        {!opps ? <Loading /> : opps.length === 0 ? <Empty label="NO_OPPORTUNITIES · run Scan Feed" /> : opps.map((o) => {
          const [sl, sc] = SEV[o.severity] || [o.severity, "#94A3AD"];
          return (
            <div key={o.id} className="border border-[#DFDAD0] bg-white p-5" data-testid={`opp-card-${o.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[9px] border border-[#DFDAD0] px-1 text-[#1C2530]/50">{o.source}</span>
                    <span className="font-mono text-[9px] uppercase px-1 border" style={{ color: sc, borderColor: sc }}>{sl}</span>
                    <span className="font-mono text-[9px] uppercase px-1 border border-[#3E8F99] text-[#3E8F99]">{o.training_category}</span>
                  </div>
                  <div className="text-sm font-medium text-[#1C2530]">{o.org_name} · <span className="text-[#1C2530]/60">{o.industry}</span></div>
                  <div className="text-xs text-[#1C2530]/70 mt-1">{o.headline}</div>
                  <div className="text-xs text-[#1C2530]/60 mt-1 italic">{o.event_summary}</div>
                </div>
                <div>
                  {o.status === "pending_review" && <span className="font-mono text-[10px] uppercase text-[#E2711D] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Pending Review</span>}
                  {o.status === "approved" && <button onClick={() => nav(`/leads/${o.lead_id}`)} className="font-mono text-[10px] uppercase text-[#2F7A3E] hover:underline">Approved → view lead</button>}
                  {o.status === "discarded" && <span className="font-mono text-[10px] uppercase text-[#94A3AD]">Discarded</span>}
                </div>
              </div>
              {o.status === "pending_review" && <ReviewForm opp={o} onApprove={approve} onDiscard={discard} busy={busy} />}
              {o.status === "approved" && o.supervisor_input && (
                <div className="mt-3 pt-3 border-t border-[#DFDAD0] font-mono text-[11px] text-[#1C2530]/60">
                  <Clock className="w-3 h-3 inline mr-1" /> {o.reviewed_by} · modules: {o.supervisor_input.modules} · {o.supervisor_input.delivery_mode}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
}
